// ISDP CUSTOM: DevSecOps Hub auth (client side of the BFF). The browser only ever holds an opaque
// session id (sessionStorage) — the real Okta tokens live in the DevHub backend. Login redirects to the
// DevSecOps Hub Okta app; the ?code callback is handled by DevSecOpsAuthCallback.
import { configApiRef, fetchApiRef, useApi } from '@backstage/core-plugin-api';
import { useCallback, useContext, useEffect, useState, createContext } from 'react';

export const DSO_SESSION_KEY = 'dso-session';

// The signed-in user's effective DevSecOps global role, provided by DevSecOpsAuthGate. Components gate
// write controls on it (read-only behaviour); the backend enforces authorization independently.
export const DsoRoleContext = createContext<string>('neutral');
export const useDsoRole = () => useContext(DsoRoleContext);
export const useCanEdit = () => ['editor', 'admin'].includes(useContext(DsoRoleContext));

export const getDsoSession = (): string | null => {
  try { return sessionStorage.getItem(DSO_SESSION_KEY); } catch { return null; }
};
export const setDsoSession = (id: string) => {
  try { sessionStorage.setItem(DSO_SESSION_KEY, id); } catch { /* ignore */ }
};
export const clearDsoSession = () => {
  try { sessionStorage.removeItem(DSO_SESSION_KEY); } catch { /* ignore */ }
};

// Where to come back to after the Okta bounce. The Okta app's redirect URI is the DevHub
// ROOT (APP_BASE_URL + "/"), so the browser always returns to "/" and the destination has to
// be remembered here — otherwise every login lands on the hub default ("My Projects"),
// whatever the user actually clicked. Only Self Service paths are stored, so a stray ?code on
// some other page cannot bounce someone into the hub.
const DSO_RETURN_KEY = 'devhub-dso-return-to';
/** Default landing for Self Service when there is nothing to return to: Create. */
export const DSO_DEFAULT_RETURN = '/self-service/create';

export const rememberDsoReturnPath = (path: string) => {
  try {
    if (path.startsWith('/self-service')) sessionStorage.setItem(DSO_RETURN_KEY, path);
  } catch { /* ignore */ }
};
/** Reads AND clears the remembered path, so it can never be reused by a later login. */
export const takeDsoReturnPath = (): string => {
  try {
    const p = sessionStorage.getItem(DSO_RETURN_KEY);
    sessionStorage.removeItem(DSO_RETURN_KEY);
    return p && p.startsWith('/self-service') ? p : DSO_DEFAULT_RETURN;
  } catch {
    return DSO_DEFAULT_RETURN;
  }
};

export interface DsoAuthState {
  loading: boolean;
  authenticated: boolean;
  email?: string;
  role: string;
  login: () => Promise<void>;
  devLogin: () => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => void;
}

export function useDevSecOpsAuth(): DsoAuthState {
  const config = useApi(configApiRef);
  const { fetch } = useApi(fetchApiRef);
  const base = `${config.getString('backend.baseUrl')}/api/devsecops`;
  const [loading, setLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);
  const [email, setEmail] = useState<string | undefined>();
  const [role, setRole] = useState<string>('neutral');
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const sid = getDsoSession();
        const resp = await fetch(`${base}/auth/status`, {
          headers: sid ? { 'x-dso-session': sid } : {},
        });
        const data = await resp.json();
        if (!cancelled) {
          setAuthenticated(!!data.authenticated);
          setEmail(data.user?.email);
          setRole(data.user?.role ?? 'neutral');
        }
      } catch {
        if (!cancelled) setAuthenticated(false);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [base, fetch, tick]);

  const login = useCallback(async () => {
    const resp = await fetch(`${base}/auth/login-url`);
    const { url } = await resp.json();
    // Remember where we were before leaving the app, so the callback can return here
    // instead of dumping everyone on the hub's default section.
    rememberDsoReturnPath(window.location.pathname + window.location.search);
    window.location.href = url;
  }, [base, fetch]);

  // DEV ONLY: headless session (no Okta) — used on localhost so Self Service is entered with the same
  // single DevHub login (no second sign-in). Backed by the stage global-admin test key; 404s in prod.
  const devLogin = useCallback(async () => {
    const resp = await fetch(`${base}/auth/dev-login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    });
    if (!resp.ok) throw new Error('dev-login unavailable');
    const data = await resp.json();
    if (!data.sessionId) throw new Error('dev-login returned no session');
    setDsoSession(data.sessionId);
    setTick(t => t + 1);
  }, [base, fetch]);

  const logout = useCallback(async () => {
    const sid = getDsoSession();
    // Invalidate the server-side session, then clear locally so the sign-in gate reappears. We don't
    // bounce to the Okta logout (which would end the whole DevHub SSO session) — this logs out of the
    // DevSecOps section only; re-login is a silent Okta SSO bounce.
    try {
      await fetch(`${base}/auth/logout`, { method: 'POST', headers: sid ? { 'x-dso-session': sid } : {} });
    } catch { /* best effort */ }
    clearDsoSession();
    setAuthenticated(false);
    setRole('neutral');
  }, [base, fetch]);

  return { loading, authenticated, email, role, login, devLogin, logout, refresh: () => setTick(t => t + 1) };
}
